import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from 'lucide-react';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function AccountWithdraw() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('INR'); // Default active tab is "INR"
  const [paymentMethod, setPaymentMethod] = useState('bank'); // Default payment method is bank
  const [formErrors, setFormErrors] = useState({});
  
  const handleGoBack = () => {
    navigate(-1);
  };

  // Yup validation schema
  const validationSchema = Yup.object({
    mobileNo: Yup.string()
      .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    amount: Yup.number()
      .positive("Amount must be positive")
      .required("Amount is required")
      .min(1, "Amount must be greater than 0"),
    accountNo: Yup.number()
      .when('paymentMethod', {
        is: 'bank',
        then: Yup.number()
          .required("Account number is required")
          .positive("Account number must be a positive number"),
      }),
    upiId: Yup.string()
      .when('paymentMethod', {
        is: 'upi',
        then: Yup.string().required("UPI ID is required"),
      }),
    ifscCode: Yup.string()
      .when('paymentMethod', {
        is: 'bank',
        then: Yup.string()
          .matches(/^[A-Za-z]{4}\d{7}$/, "Invalid IFSC code")
          .required("IFSC code is required"),
      }),
  });

  // Function to handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const values = {
      mobileNo: e.target.mobileNo.value,
      amount: e.target.amount.value,
      accountNo: e.target.accountNo.value,
      upiId: e.target.upiId.value,
      ifscCode: e.target.ifscCode.value,
    };

    try {
      // Validate input using Yup
      await validationSchema.validate(values, { abortEarly: false });
      // Clear previous errors
      toast.success("Form submitted successfully!");
      // Proceed with form submission
    } catch (err) {
      // Display validation errors as toast notifications
      err.inner.forEach((error) => {
        toast.error(error.message);
      });
    }
  };

  return (
    <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
      <div className="h-screen overflow-y-scroll p-4 pb-20 flex-grow max-w-sm min-w-[375px] w-full bg-white 
            [&::-webkit-scrollbar]:w-1 
            [&::-webkit-scrollbar-track]:bg-transparent 
            [&::-webkit-scrollbar-thumb]:bg-transparent 
            scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">
        
        <div className="flex items-center justify-between mb-[30px]">
          <div className="flex items-center gap-1">
            <ChevronLeft size={16} onClick={handleGoBack} />
            <span className="font-lato font-semibold text-sm">Withdraw</span>
          </div>
          <div className="flex justify-end bg-white border-b-0 rounded-[10px] max-w-[127px] w-full">
            <div role="tablist" className="flex bg-[#536895] bg-opacity-25 rounded-[10px]">
              <a href="#" role="tab" onClick={() => setActiveTab('INR')}
                className={`${
                  activeTab === 'INR' ? 'bg-[#536895] text-white' : 'bg-transparent text-black'
                } font-lato text-sm leading-[18px] rounded-[10px] py-[10px] px-[14px] text-center w-[63px]`}
              >
                INR
              </a>
              <a href="#" role="tab" onClick={() => setActiveTab('Forex')}
                className={`${
                  activeTab === 'Forex' ? 'bg-[#536895] text-white' : 'bg-transparent text-black'
                } font-lato text-sm leading-[18px] rounded-[10px] py-[10px] px-[14px] text-center w-[63px]`}
              >
                Forex
              </a>
            </div>
          </div>
        </div>

        <div className="w-full items-center justify-between mb-[30px]">
          <form onSubmit={handleSubmit}>
            <div className="text-center bg-gray-100 rounded-[1rem] p-[1rem]">
              <span className="font-lato font-semibold text-[1rem]">
                Online Withdrawal
              </span>
              <div className="flex items-center justify-center gap-[70px] m-[25px]">
                {/* UPI Radio Button */}
                <div className="flex items-center gap-2 mb-[.125rem] min-h-[1.5rem]">
                  <input
                    type="radio"
                    id="upi"
                    name="paymentMethod"
                    value="upi"
                    checked={paymentMethod === 'upi'}
                    onChange={() => setPaymentMethod('upi')}
                    className="w-4 h-4 rounded-full bg-center bg-contain border border-gray-300 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0 mt-1"
                  />
                  <label htmlFor="upi">
                    <span>
                      <img src="./upi.svg" alt="upi-icon" />
                    </span>
                  </label>
                </div>

                {/* Bank Radio Button */}
                <div className="flex items-center gap-2 mb-[.125rem] min-h-[1.5rem]">
                  <input
                    type="radio"
                    id="bank"
                    name="paymentMethod"
                    value="bank"
                    checked={paymentMethod === 'bank'}
                    onChange={() => setPaymentMethod('bank')}
                    className="w-4 h-4 rounded-full bg-center bg-contain border border-gray-300 checked:bg-blue-600 checked:border-blue-600 flex-shrink-0 mt-1"
                  />
                  <label htmlFor="bank">
                    <span>
                      <img src="./bank.svg" alt="bank-icon" />
                    </span>
                  </label>
                </div>
              </div>

              {/* Mobile No. Field */}
              <div className="flex items-center mb-[30px]">
                <input
                  type="number"
                  id="mobileNo"
                  name="mobileNo"
                  placeholder="Mobile No."
                  className="w-full bg-gray-300 rounded-[5px] px-4 py-2"
                />
              </div>

              {/* Amount Field */}
              <div className="flex items-center mb-[30px]">
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  placeholder="Amount"
                  className="w-full bg-gray-300 rounded-[5px] px-4 py-2"
                />
              </div>

              {/* Conditionally Render Fields Based on Payment Method */}
              {paymentMethod === 'upi' && (
                <>
                  {/* UPI ID Field for UPI */}
                  <div className="flex items-center">
                    <input
                      type="text"
                      id="upiId"
                      name="upiId"
                      placeholder="UPI ID"
                      className="w-full bg-gray-300 rounded-[5px] px-4 py-2"
                    />
                  </div>
                </>
              )}

              {paymentMethod === 'bank' && (
                <>
                  {/* Account No. Field for Bank */}
                  <div className="flex items-center mb-[30px]">
                    <input
                      type="number"
                      id="accountNo"
                      name="accountNo"
                      placeholder="Account No."
                      className="w-full bg-gray-300 rounded-[5px] px-4 py-2"
                    />
                  </div>

                  {/* IFSC Code Field for Bank */}
                  <div className="flex items-center">
                    <input
                      type="text"
                      id="ifscCode"
                      name="ifscCode"
                      placeholder="IFSC CODE"
                      className="w-full bg-gray-300 rounded-[5px] px-4 py-2"
                    />
                  </div>
                </>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full mt-[30px] bg-[#536895] leading-[20px] border border-[#536895] rounded-lg text-white text-[16px] font-semibold py-[14px] px-[55px]"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}

export default AccountWithdraw;